<script setup>
import { inject, onMounted, ref } from 'vue';
import { useBIQueryBuilder } from '~/bi/composables/useBIQueryBuilder.js';
import { useBiStore } from '~/bi/store/bi.store';

const global_filters_of_component = inject('global_filters_of_component');
const bi_store = useBiStore();
const { getColumnsForSeries } = useBIQueryBuilder();
const override_map = ref({});
const filter_index = global_filters_of_component.value.findIndex(filter => filter.uid === bi_store.selected_global_filter_uid);
const $toast = inject('$toast');
const $t = inject('$t');
const widgets = (bi_store.current_grid_layout || []).filter(widget => widget.config.query.stages);
function getTablesForStage(stage, stage_index) {
  let tables = [];
  if ((stage.table || stage_index === 0) && (bi_store.all_schema_by_name[stage.table] || stage.table?.generateSeries)) {
    const table_label = stage.table?.alias || bi_store.all_schema_by_name[stage.table]?.label;
    tables = [{ label: table_label, name: stage.table, columns: stage.table?.generateSeries ? getColumnsForSeries(stage.table) : bi_store.all_schema_by_name[stage.table].columns }];
  }
  stage.joins?.forEach((join) => {
    if (bi_store.all_schema_by_name?.[join?.table?.name]) {
      const table_label = bi_store.all_schema_by_name[join.table.name].label;
      tables.push({ label: table_label, name: join.table.name, columns: bi_store.all_schema_by_name[join.table.name].columns });
    }
  });
  return tables;
}

function getStages(stages) {
  if (stages?.length > 0) {
    return stages.map((stage, index) => ({ label: `Stage ${index + 1}`, columns: stage.columns.filter(field => !field.skip).map(field => ({ ...field, type: field.field_type, label: field.alias })), tables: getTablesForStage(stage, index), filters: stage.filters }));
  }
  return [];
}

function autoFillAllWidgets(column) {
  widgets.forEach((widget) => {
    const stages = getStages(widget.config?.query?.stages);
    let field;
    for (let i = 0; i < stages.length; i++) {
      field = stages[i].columns.find(col => col.field === column.field);
      if (!field) {
        field = stages[i].tables.find(table => table.columns.find(col => col.field === column.field));
      }
      if (field) {
        global_filters_of_component.value[filter_index].widgets[widget.uid] = { field: column.table_name ? column.field : column.alias, alias: column.alias, stage_index: i };
        break;
      }
    }
  });
}

function extractTableColumns(tables) {
  return tables.reduce((acc, table) => {
    acc.push(...table.columns);
    return acc;
  }, []);
}

function canAutoFill(column) {
  return widgets.find(widget => getStages(widget.config?.query?.stages).find(stage => stage.columns?.find(col => col.field === column.field) || extractTableColumns(stage.tables)?.find(col => col.field === column.field)));
}

function onColumnSelected(column, widget) {
  if (Object.keys(global_filters_of_component.value[filter_index].widgets).length === 0 && widgets.length > 1 && canAutoFill(column)) {
    $toast({ title: $t('Autofill'), text: $t('Automatically map the filter to the widgets having same fields'), type: 'info', action_button_text: $t('Auto fill'), dismiss_button_text: $t('Cancel'), onClick: () => autoFillAllWidgets(column) });
  }
  global_filters_of_component.value[filter_index].widgets[widget.uid] = { field: column.table_name ? column.field : column.alias, alias: column.alias, stage_index: column.stage_index };
  // Initialize override true if toggle should be shown and no override set yet
  if (shouldShowToggle(widget)) {
    if (global_filters_of_component.value[filter_index].widgets[widget.uid].override === undefined) {
      global_filters_of_component.value[filter_index].widgets[widget.uid].override = true;
      override_map.value[widget.uid] = true;
    }
    else {
      override_map.value[widget.uid] = global_filters_of_component.value[filter_index].widgets[widget.uid].override;
    }
  }
  else {
    // ensure override_map doesn't carry stale value
    delete override_map.value[widget.uid];
  }
}

function clearSelection(widget) {
  delete global_filters_of_component.value[filter_index].widgets[widget.uid];
  delete override_map.value[widget.uid];
}

function getSupportedTypes() {
  const type = global_filters_of_component.value[filter_index].general.filter_type;
  if (type === 'text')
    return ['text', 'string', 'varchar', 'char'];
  if (type === 'number')
    return ['integer', 'float', 'double', 'numeric', 'bigint', 'smallint', 'decimal', 'number'];
  if (type === 'date_range')
    return ['date', 'timestamp'];
  if (type === 'date_interval')
    return ['date', 'timestamp'];
  return [];
}

function shouldShowToggle(widget) {
  const mapping = global_filters_of_component.value[filter_index].widgets[widget.uid];
  if (!mapping)
    return false;
  const stages = getStages(widget.config?.query?.stages);
  const stage = stages?.[mapping.stage_index];
  const matches = stage?.filters?.find(f => f.field === mapping.field);
  return !!(matches);
}

// initialize override_map from existing global_filters
onMounted(() => {
  widgets.forEach((widget) => {
    const mapping = global_filters_of_component.value[filter_index].widgets[widget.uid];
    if (mapping && mapping.override !== undefined) {
      override_map.value[widget.uid] = mapping.override;
    }
    else if (mapping && shouldShowToggle(widget)) {
      // default true
      override_map.value[widget.uid] = true;
      global_filters_of_component.value[filter_index].widgets[widget.uid].override = true;
    }
  });
});

function updateOverrideMap(widget, override = true) {
  override_map.value[widget.uid] = override;
  global_filters_of_component.value[filter_index].widgets[widget.uid].override = override;
}

function setDefaultOverrideValue(widget) {
  if (global_filters_of_component[filter_index]?.widgets?.[widget.uid] && !Object.hasOwn(global_filters_of_component[filter_index].widgets[widget.uid], 'override')) {
    global_filters_of_component[filter_index].widgets[widget.uid].override = true;
  }
}
</script>

<template>
  <div class="h-[calc(100%-20px)] overflow-auto">
    <Vueform v-model="override_map" :sync="true" size="sm">
      <div class="col-span-12">
        <div v-for="widget in widgets" :key="widget.uid" class="grid grid-cols-12 gap-x-2 gap-y-0 py-4 group">
          <div class="col-span-5 text-gray-900 text-sm font-medium">
            {{ widget.name }}
          </div>
          <div class="col-span-6 text-right">
            <widgets-mapping-dropdown :stages="getStages(widget.config?.query?.stages)" :selected-field="global_filters_of_component[filter_index].widgets[widget.uid]" :supported-types="getSupportedTypes()" @selected="onColumnSelected($event, widget)" />
          </div>
          <div class="col-span-1">
            <IconHawkTrashOne class="text-gray-500 hover:text-gray-600 cursor-pointer hidden group-hover:block" @click="clearSelection(widget)" />
          </div>
          <ToggleElement
            v-if="shouldShowToggle(widget)"
            class="mt-1 !text-xs"
            :name="widget.uid" :label="$t('This filter already exists in the widget. Do you want to override it?')"
            :add-classes="{
              ElementLabel: {
                wrapper: 'text-xs',
              },
            }"
            :default="override_map[widget.uid] ?? true"
            :columns="{
              default: { container: 12, label: 10, wrapper: 1 },
              sm: { container: 12, label: 10, wrapper: 1 },
              md: { container: 12, label: 10, wrapper: 1 },
            }"
            @mounted="setDefaultOverrideValue(widget)"
            @change="updateOverrideMap(widget, $event)"
          />
        </div>
      </div>
    </Vueform>
  </div>
</template>
